/**
 * EventPlanService
 * Events hub Stage 2 plans: a playbook resolved for one event (dates, assignees and warnings), edited
 * as a draft, then approved in one click. Approval queues the ticked items; EventManagement creates
 * each one in its own app as the approver and links it back to the event.
 *
 * Generated work is never moved when the event moves: the plan becomes OutOfDate instead.
 *
 * Route: events/api/events/{id}/plans
 *  - read: Event.View or the event's coordinator
 *  - create and edit a draft: Event.Plan.Approve, Event.Playbook.Manage or the coordinator
 *  - approve, retry and skip: Event.Plan.Approve or the coordinator
 */
import { BaseService } from '../base/BaseService';
import { unwrapEventsResponse as unwrap } from './unwrapEventsResponse';
export class EventPlanService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? EventPlanService.DEFAULT_SERVICE_PATH,
            basePath: EventPlanService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** GET api/events/{id}/plans. Every plan of the event, newest first, with items. */
    async list(eventId) {
        const res = await this.listRaw(eventId);
        return res.data ?? [];
    }
    listRaw(eventId) {
        return this.http.get(this.buildUrl(`${eventId}/plans`));
    }
    /** GET api/events/{id}/plans/{planId}. */
    async get(eventId, planId) {
        return unwrap(await this.getRaw(eventId, planId), `Plan ${planId} not found`);
    }
    getRaw(eventId, planId) {
        return this.http.get(this.buildUrl(`${eventId}/plans/${planId}`));
    }
    /** POST api/events/{id}/plans. A Draft plan with resolved items; no playbook = an empty ad hoc plan. */
    async create(eventId, playbookId) {
        return unwrap(await this.createRaw(eventId, { playbookID: playbookId ?? null }), `Could not create a plan for event ${eventId}`);
    }
    createRaw(eventId, request) {
        return this.http.post(this.buildUrl(`${eventId}/plans`), request);
    }
    /** POST api/events/{id}/plans/{planId}/items (Draft only). The item is resolved like a playbook item. */
    async addItem(eventId, planId, item) {
        return unwrap(await this.addItemRaw(eventId, planId, item), 'Could not add the item');
    }
    addItemRaw(eventId, planId, item) {
        return this.http.post(this.buildUrl(`${eventId}/plans/${planId}/items`), item);
    }
    /** PUT api/events/{id}/plans/{planId}/items/{itemId} (Draft only). Left out = unchanged. */
    async updateItem(eventId, planId, itemId, request) {
        return unwrap(await this.updateItemRaw(eventId, planId, itemId, request), `Could not update item ${itemId}`);
    }
    updateItemRaw(eventId, planId, itemId, request) {
        return this.http.put(this.buildUrl(`${eventId}/plans/${planId}/items/${itemId}`), request);
    }
    /** DELETE api/events/{id}/plans/{planId} (Draft only). The plan becomes Cancelled. */
    async cancel(eventId, planId) {
        return unwrap(await this.cancelRaw(eventId, planId), `Could not cancel plan ${planId}`);
    }
    cancelRaw(eventId, planId) {
        return this.http.delete(this.buildUrl(`${eventId}/plans/${planId}`));
    }
    /** POST api/events/{id}/plans/{planId}/approve. Queues the ticked items; each is created as the approver. */
    async approve(eventId, planId) {
        return unwrap(await this.approveRaw(eventId, planId), `Could not approve plan ${planId}`);
    }
    approveRaw(eventId, planId) {
        return this.http.post(this.buildUrl(`${eventId}/plans/${planId}/approve`));
    }
    /** POST api/events/{id}/plans/{planId}/items/{itemId}/retry. Queues a failed item again (same idempotency key). */
    async retryItem(eventId, planId, itemId) {
        return unwrap(await this.retryItemRaw(eventId, planId, itemId), `Could not retry item ${itemId}`);
    }
    retryItemRaw(eventId, planId, itemId) {
        return this.http.post(this.buildUrl(`${eventId}/plans/${planId}/items/${itemId}/retry`));
    }
    /** POST api/events/{id}/plans/{planId}/items/{itemId}/skip. Gives up on a failed item. */
    async skipItem(eventId, planId, itemId) {
        return unwrap(await this.skipItemRaw(eventId, planId, itemId), `Could not skip item ${itemId}`);
    }
    skipItemRaw(eventId, planId, itemId) {
        return this.http.post(this.buildUrl(`${eventId}/plans/${planId}/items/${itemId}/skip`));
    }
}
EventPlanService.DEFAULT_SERVICE_PATH = 'events';
EventPlanService.BASE_PATH = 'api/events';
//# sourceMappingURL=EventPlanService.js.map